<template>
  <Loader :value="promise" :message="$t('loading', { resource: $tc('form', 2) }) + '...'" class="form-submits">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #actions>
          <Actions v-if="form.id" placement="form-submits" :query="{ formId: form.id }" />
          <ButtonComponent
            tag="router-link"
            theme="primary"
            :to="{
              name: 'form-preview',
              params: { id: form.id },
            }"
            :label="$t('add')"
            :icon="['fal', 'plus']"
          />
        </template>
      </HeaderComponent>

      <DataTable
        v-if="items.length"
        :sticky="false"
        :value="items"
        :columns="columns"
        :row-wrapper="
          item => ({
            component: 'router-link',
            props: {
              to: {
                name: `form-submit-mutate`,
                params: { id: item.id },
              },
              style: { color: 'inherit' },
            },
          })
        "
      >
        <template v-for="field in columns" #[field.id]="{ item }">
          <FileComponent
            v-if="$path(`data.${field.id}.size`, item)"
            class="text--small"
            :value="$path(`data.${field.id}`, item)"
            readonly
          />

          <span
            v-else-if="item.data && typeof item.data[field.id] === 'string' && item.data[field.id].startsWith('https')"
          >
            <a @click.stop="() => {}" target="_blank" :href="item.data[field.id]"> {{ item.data[field.id] | Limit }}</a>
          </span>

          <span v-else-if="item.data && typeof item.data[field.id] === 'string'">
            {{ item.data[field.id] | Limit }}</span
          >

          <span v-else-if="item.data">{{ item.data[field.id] }}</span>
        </template>

        <template #deliveryLog="{ item }">
          <template v-for="log in item.deliveryLogs">
            <DeliveryLog class="mt-1" :resource="$tc('inquiry', 1)" :value="log" />
          </template>
        </template>

        <template #createdAt="{ item }">
          <small class="fade">{{ $path('createdAt', item) | Moment('L, LT') }}</small>
        </template>
      </DataTable>

      <InfiniteLoading @infinite="next" :identifier="infiniteId">
        <template #spinner>
          <div class="infinite-loading-spinner">
            <span class="infinite-loading-spinner--text">{{ $t('loading', { resource: '...' }) }}</span>
            <FontAwesomeIcon
              class="infinite-loading-spinner--spinner"
              :icon="['fal', 'spinner-third']"
              spin
              size="2x"
            />
          </div>
        </template>
        <template #no-more>{{ $t('noMore', { x: $tc('inquiry', 2).toLowerCase() }) }}</template>
        <template #no-results>
          <EmptyState
            :title="$t('noX', { x: $tc('inquiry', 2).toLowerCase() })"
            :icon="['fal', 'address-card']"
            :subtitle="$t('filterEmptyState')"
          />
        </template>
      </InfiniteLoading>
    </div>
  </Loader>
</template>

<script>
import { Moment, Limit } from '@/filters'
import Pagination from '@/mixins/Pagination'
import API from '@/api/Project'

import HeaderComponent from '@/components/Header'
import Actions from '@/components/Actions'
import Card from '@/components/Card'
import DataTable from '@/components/DataTable'
import Badge from '@/components/Badge'
import EmptyState from '@/components/EmptyState'
import InfiniteLoading from 'vue-infinite-loading'
import DeliveryLog from '@/components/DeliveryLog'
import { FileComponent } from '@kvass/file'

const LabelMap = {
  'contact.name': 'name',
  'contact.email': 'email',
  'contact.phone': 'phone',
}

const hideFields = ['privacy']
export default {
  mixins: [Pagination],
  filters: {
    Moment,
    Limit,
  },
  props: {
    id: String,
  },

  data() {
    return {
      form: {},
      promise: null,
      exportItems: [
        { value: 'name', label: this.$t('name') },
        { value: 'email', label: this.$t('email') },
        { value: 'type', label: this.$tc('type', 1) },
        { value: 'phone', label: this.$t('phone') },
        { value: 'tags', label: this.$tc('tag', 2) },
        { value: 'createdAt', label: this.$t('createdAt') },
      ],
    }
  },
  computed: {
    dataComp() {
      return this.data
    },
    fields() {
      return this.$path('fields', this.form) || []
    },
    columns() {
      return [
        ...this.fields.map(i => {
          if (hideFields.includes(i.key) || hideFields.includes(i.component)) return

          if (i.component === 'position') {
            return [
              {
                id: 'address',
                label: this.$t('address'),
              },
              {
                id: 'placement',
                label: this.$t('placement'),
              },
            ]
          }

          if (i.component === 'lead') {
            return [
              {
                id: 'contact.name',
                label: this.$t('name'),
              },
              {
                id: 'contact.email',
                label: this.$t('email'),
              },
              {
                id: 'contact.phone',
                label: this.$t('phone'),
              },
              {
                id: 'comment',
                label: this.$tc('message', 1),
              },
            ]
          }

          return {
            id: i.key,
            label: this.$tc(LabelMap[i.key] || ((this.items || []).find(p => p.key === i.key) || {}).label || i.label),
          }
        }),
        { id: 'deliveryLog', label: this.$t('deliveryStatus'), align: 'right' },

        { id: 'createdAt', label: this.$t('createdAt'), align: 'right' },
      ]
        .flat()
        .filter(Boolean)
    },
    breadcrumb() {
      return [
        {
          title: this.reference.name,
          action: { name: `${this.reference.onModel.toLowerCase()}-dashboard`, params: { id: this.reference.id } },
        },
        { title: this.form.title },
      ]
    },

    reference() {
      //page or project

      return this.$path('reference', this.form) || { onModel: 'Project' }
    },
    items() {
      return this.item.docs
    },
  },
  methods: {
    fetch(v) {
      if (this.item.pages === 0 || (this.item.pages && this.item.page === this.item.pages)) return Promise.resolve()

      this.promise = API.get(
        { id: this.id, paginate: this.paginate },

        {
          query: 'FormSubmits',
        },
      ).then(res => {
        const { submitsPaginate, ...rest } = res
        this.form = rest
        return submitsPaginate
      })

      return this.promise
    },
  },
  metaInfo() {
    return {
      title: `${this.$t('form')} - ${this.form.title}`,
    }
  },
  components: {
    Card,
    HeaderComponent,
    Actions,
    DataTable,
    Badge,
    EmptyState,
    InfiniteLoading,
    DeliveryLog,
    FileComponent,
  },
}
</script>

<style lang="scss">
.form-submits {
  background-color: $background-grey;
  .data-table {
    overflow-y: auto;
    &__row {
      white-space: nowrap;
    }
  }
  [data-cell-id='deliveryLog'] {
    flex-direction: column;
    align-items: start;
  }
}
</style>
